import { useSessionPolicy } from './use-session-policy';
import { useSessionTimeout } from './use-session-timeout';
import { SessionTimeoutDialog } from './session-timeout-dialog';
import type { SessionActivitySubscriber } from './use-session-timeout';
import type { SessionPolicy } from './use-session-policy';

type SessionTimeoutClockProps = {
  policy: SessionPolicy;
  expiresAt?: number;
  extend: () => Promise<boolean>;
  onExpired: () => void;
  subscribeToActivity?: SessionActivitySubscriber;
};

function SessionTimeoutClock(props: SessionTimeoutClockProps) {
  const timeout = useSessionTimeout({
    idleTimeoutHours: props.policy.idleTimeoutHours,
    warningMinutes: props.policy.warningMinutes,
    expiresAt: props.expiresAt,
    onExtend: props.extend,
    onExpired: props.onExpired,
    subscribeToActivity: props.subscribeToActivity,
  });

  return (
    <SessionTimeoutDialog
      open={timeout.showWarning}
      remainingSeconds={timeout.remainingSeconds}
      onStaySignedIn={timeout.onStaySignedIn}
      onDismiss={timeout.onHideWarning}
    />
  );
}

export type SessionTimeoutProps = {
  /** Where this host sends a tab whose window ran out — an SPA route, or a document redirect. */
  onExpired: () => void;
  subscribeToActivity?: SessionActivitySubscriber;
};

/**
 * The idle warning, whole: it reads the organization's own window, counts it down, and offers
 * to keep the session. One component for every host — the SPA renders it inside its shell, and
 * the server-rendered pages mount it on their own — so the same clock says the same thing.
 * Nothing renders until the session answers: the organization's window is the only window a
 * countdown may run on.
 */
export function SessionTimeout(props: SessionTimeoutProps) {
  const sessionPolicy = useSessionPolicy();

  let clock = null;
  if (sessionPolicy.policy) {
    clock = (
      <SessionTimeoutClock
        policy={sessionPolicy.policy}
        expiresAt={sessionPolicy.expiresAt}
        extend={sessionPolicy.extend}
        onExpired={props.onExpired}
        subscribeToActivity={props.subscribeToActivity}
      />
    );
  }

  return clock;
}
